import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'

import { EmployerOnboardingPage, useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { useCreateQuoteAllowances } from 'components/hooks/quotes/useCreateQuoteAllowances'
import { useQuoteComplianceChecks } from 'components/hooks/quotes/useQuoteComplianceChecks'
import { type Allowances } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import { Button, TextLink, Tooltip } from 'slab'

import { useEmployer } from '../EmployerProvider'
import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'
import { AllowanceStrategies } from './types'

export function QuoteReviewButton ({ width = 'full' }: { width?: 'full' | 'auto' }) {
  const { employer } = useEmployer()
  const { trigger } = useFormContext<Allowances>()
  const { navigateToStep } = useOnboardingStep()

  const {
    quoteId,
    allowanceStrategy,
    allowances,
    filterValues,
    selectedCoverageLevel,
    quoteResultsByCoverageLevel,
    isUsingWizard,
    isOnFinalWizardStep,
  } = useSelfServeExplorerContext()

  const { shouldDisableReviewButton } = useQuoteComplianceChecks()

  const createQuoteAllowancesMutation = useCreateQuoteAllowances({
    prospectId: employer!.quoting_prospect_id!,
    quoteId,
    removeExisting: true,
  })

  const onReviewClick = async () => {
    try {
      if (allowanceStrategy === AllowanceStrategies.Flat) {
        const isValid = await trigger()
        if (!isValid) {
          toast.error('Invalid allowances: please fix the errors before proceeding')
          return
        }

        await createQuoteAllowancesMutation.mutateAsync(allowances)
        toast.success('Quote saved!')
        navigateToStep(EmployerOnboardingPage.QUOTE_REVIEW)
      } else {
        const coverageLevel =
          quoteResultsByCoverageLevel![selectedCoverageLevel]

        await createQuoteAllowancesMutation.mutateAsync([
          {
            dynamic_config: {
              coverage_percentile: Number(
                coverageLevel!.benchmark!.coverage_percentile
              ),
              benchmark: `on_thatch_${coverageLevel!.benchmark!.metal_tier}`,
              employee_contribution_percentage:
                filterValues.employerEmployeeContribution * 100,
              spouse_contribution_percentage:
                filterValues.employerSpouseContribution * 100,
              dependent_contribution_percentage:
                filterValues.employerChildContribution * 100,
            },
          },
        ])
        navigateToStep(EmployerOnboardingPage.QUOTE_REVIEW)
      }
    } catch (error) {
      toast.error(`Failed to save quote: ${error}`)
    }
  }

  return (
    <Tooltip
      enabled={shouldDisableReviewButton}
      content={
        <div>
          <p>Applicable Large Employers (ALEs) must provide an allowance that is in compliance with affordability and fairness requirements.</p>
          <p className='mt-2'>
            <TextLink
              href='https://support.thatch.com/affordability'
              target='_blank'
              rel='noopener noreferrer'
              color='inherit'
            >
              Learn more about affordability requirements
            </TextLink>
          </p>
        </div>
      }
    >
      <Button
        width={width}
        variant={isUsingWizard && !isOnFinalWizardStep ? 'secondary' : 'primary'}
        size='large'
        onClick={onReviewClick}
        disabled={createQuoteAllowancesMutation.isPending || shouldDisableReviewButton}
      >
        Review
      </Button>
    </Tooltip>
  )
}
